<template>
  <div class="card">
    <div class="card-header">
      <h3>Динамика новостей по источнику</h3>
      <div class="card-actions">
        <RefreshIcon @click="refresh" />
        <CalendarInput v-model="localDate" />
        <SourcesDropdown
          v-model:selectedSource="selectedSource"
        />
      </div>
    </div>
    <div 
      v-if="selectedSource && hasData"
      class="chart-wrapper"
    >
      <Apexchart 
        type="bar" 
        height="300" 
        :options="chartOptions" 
        :series="chartSeries"
      />
    </div>
    <div 
      v-else
      class="no-data-placeholder"
    >
      <div class="no-data-content">
        <template v-if="!selectedSource">
          <h4>Источник не выбран</h4>
          <p>Выберите источник из списка для построения графика</p>
        </template>
        <template v-else>
          <h4>Нет данных</h4>
          <p>За выбранный период данных не найдено</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import VueApexCharts from "vue3-apexcharts";
import CalendarInput from "@/components/UI/CalendarInput.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import { useAnalyticsStore } from "@/stores/analytics";
import SourcesDropdown from "@/components/analytics/SourcesDropdown.vue";
import { formatDateForX, formatDateForRequest } from "@/tools/time";

const route = useRoute();
const Apexchart = VueApexCharts;
const analyticsStore = useAnalyticsStore();

const localDate = ref([new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]);
const selectedSource = ref(null);

watch(localDate, (newVal) => {
  if (newVal?.[0] && newVal?.[1]) {
    analyticsStore.fetchNewsBySource({
      from: formatDateForRequest(newVal[0]),
      to: formatDateForRequest(newVal[1]),
      sourceIds: selectedSource.value ? selectedSource.value.id : ""
    });
  }
});

watch(selectedSource, (newVal) => {
  const params = {
    sourceIds: newVal ? newVal.id : ""
  };

  if (localDate.value?.[0] && localDate.value?.[1]) {
    params.from = formatDateForRequest(localDate.value[0]);
    params.to = formatDateForRequest(localDate.value[1]);
  }

  analyticsStore.fetchNewsBySource(params);
}, { deep: true });

const refresh = async () => {
  const params = {
    from: formatDateForRequest(localDate.value[0]),
    to: formatDateForRequest(localDate.value[1])
  };
  
  let sourceId = null;
  if (route.query.sourceId) {
    sourceId = route.query.sourceId;
    selectedSource.value = { id: sourceId };
  } else if (selectedSource.value) {
    sourceId = selectedSource.value.id;
  }
  if (sourceId) {
    params.sourceIds = sourceId;
  }

  await analyticsStore.fetchNewsBySource(params);
};

const hasData = computed(() => analyticsStore.newsBySource && analyticsStore.newsBySource.length > 0);

const chartSeries = computed(() => [{
  name: "Новости",
  data: analyticsStore.newsBySource?.length ? analyticsStore.newsBySource.map(item => item.count) : []
}]);

const chartOptions = computed(() => ({
  chart: {
    type: "bar",
    toolbar: { show: false },
    fontFamily: "Inter, sans-serif"
  },
  colors: ["#1F9AFF"],
  plotOptions: {
    bar: {
      borderRadius: 4,
      columnWidth: "50%",
      dataLabels: { position: "top" }
    }
  },
  dataLabels: {
    enabled: true,
    offsetY: -20,
    style: { fontSize: "12px", colors: ["#304758"] }
  },
  xaxis: {
    categories: analyticsStore.newsBySource?.length
      ? analyticsStore.newsBySource.map(item => formatDateForX(item.createdAt))
      : [],
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { colors: "#7B87A5" } }
  },
  yaxis: {
    labels: { style: { colors: "#7B87A5" } }
  },
  grid: {
    borderColor: "#F1F1F1",
    strokeDashArray: 4
  },
  tooltip: {
    theme: "light"
  },
  legend: {
    show: false
  }
}));

onMounted( () => {
  refresh();
});
</script>

<style scoped>
.card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.03);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #1A1D1F;
}

.card-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.source-select {
  appearance: none;
  border: 1px solid #EFEFEF;
  border-radius: 8px;
  padding: 6px 32px 6px 12px;
  height: 42px;
  font-size: 13px;
  color: #7B87A5;
  outline: none;
  background: #F9FAFB;
  cursor: pointer;
}

.select-icon {
  position: absolute;
  right: 10px;
  top: 12px;
  pointer-events: none;
  width: 16px;
  height: 16px;
  color: #7B87A5;
}

/* Chart Legend */
.chart-legend {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  background: #1F9AFF;
  border-radius: 4px;
}

.legend-text {
  font-size: 13px;
  color: #1A1D1F;
}

.no-data-placeholder {
  height: 300px;
  background: #F8F9FB;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.no-data-content h4 {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #304758;
}

.no-data-content p {
  margin: 0;
  font-size: 14px;
  color: #7B87A5;
}
</style>
